import { useCallback } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import { stringifyUrl } from "query-string";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";

export const useCompanyCustomHourlyRates = (id, config) => {
  return useQuery({
    ...config,
    queryKey: ["companies", id, "custom_hourly_rates"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/companies/${id}/custom-hourly-rates`,
      }).then(function (response) {
        if (Array.isArray(response)) {
          return {};
        } else {
          return response;
        }
      }),
  });
};

export const useCompanyMembersResponsibilities = (id, config) => {
  return useQuery({
    ...config,
    queryKey: ["companies", id, "responsibilities"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/companies/${id}/members-responsibilities`,
      }),
  });
};

export const useCompanyNotes = (config) => {
  return useQuery({
    ...config,
    queryKey: ["companies", "notes"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/companies/notes`,
      }),
  });
};

export const useCompanyMetrics = (id, enabled) => {
  return useQuery({
    enabled: enabled,
    queryKey: ["company-currencies"],
    queryFn: () =>
      fetch({
        url: `${Angie.api_url}/companies/${id}/invoices-metrics`,
      }),
  });
};

export const useArchivedCompanies = (params) => {
  const fetchData = useCallback(
    ({ pageParam }) => {
      return fetch({
        url: stringifyUrl(
          {
            url: `${Angie.api_url}/companies/archive-for-screen`,
            query: { cursor: pageParam, ...params },
          },
          { arrayFormat: "bracket" }
        ),

        withHeaders: true,
      }).then((response) => {
        return {
          companies: response._parsedBody,
          cursor: response._headers["x-angie-paginationnextcursor"],
        };
      });
    },
    [params]
  );

  return useInfiniteQuery({
    queryKey: ["archived-companies", params],
    queryFn: (params) => fetchData(params),
    keepPreviousData: true,
    getNextPageParam: (lastPage) => (lastPage.cursor ? lastPage.cursor : undefined),
  });
};

export const useMarkTimeAndExpensesAsPaid = (companyId, currencyId, type) => {
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/companies/${companyId}/mark-payment-time`,
        method: "post",
        body: { currency_id: currencyId, type },
      }),
  });
};

export const useGetInvoicesForMarkPayment = (companyId, currencyId) => {
  return useQuery({
    queryKey: ["get-mark-payments-invoices", companyId, currencyId],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/companies/${companyId}/mark-payments?currency_id=${currencyId}`,
        signal,
      }),
  });
};

export const useMarkPayments = (companyId) => {
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/companies/${companyId}/mark-payments`,
        method: "post",
        body: { invoices: data.invoices, notify_client: data.notify_client },
      }),
  });
};

export const useGetOverdueInvoices = (companyId, currencyId) => {
  return useQuery({
    queryKey: ["get-overdue-invoices", companyId, currencyId],
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/companies/${companyId}/overdue-invoices?currency_id=${currencyId}`,
        signal,
      }),
  });
};

export const useSendOverdueInvoicesReminder = (companyId) => {
  return useMutation({
    mutationFn: (invoices) =>
      fetch({
        url: `${Angie.api_url}/companies/${companyId}/overdue-invoices`,
        method: "post",
        body: invoices,
      }),
  });
};

export const useCompaniesHasDocuments = (params, config) => {
  return useQuery({
    ...config,
    queryKey: ["company-documents", params],
    queryFn: ({ signal }) =>
      fetch({
        url: stringifyUrl(
          {
            url: `${Angie.api_url}/companies/has-documents`,
            query: { status: params?.status, ids: params?.ids },
          },
          { arrayFormat: "bracket" }
        ),
        signal,
      }),
  });
};
